<template>
	<v-card elevation="1" rounded="lg" class="fill-height d-flex flex-column">
		<v-card-title class="d-flex align-center py-3 px-4">
			<v-icon color="primary" size="20" class="me-2">mdi-calendar-clock</v-icon>
			<span class="text-subtitle-2">{{ $t("dashboard.upcomingRituals.title") }}</span>
		</v-card-title>

		<v-divider />

		<v-card-text class="flex-grow-1 pa-0">
			<!-- Loading -->
			<div v-if="loading" class="pa-4">
				<v-skeleton-loader v-for="i in 3" :key="i" type="list-item-two-line" class="mb-2" />
			</div>

			<!-- Empty state -->
			<div v-else-if="rituals.length === 0" class="d-flex flex-column align-center justify-center pa-6" style="min-height: 140px">
				<v-icon size="40" color="grey-lighten-1" class="mb-2">mdi-calendar-blank</v-icon>
				<span class="text-body-2 text-medium-emphasis">{{ $t("dashboard.upcomingRituals.noRituals") }}</span>
			</div>

			<!-- List -->
			<v-list v-else density="compact" class="py-0">
				<v-list-item
					v-for="ritual in rituals"
					:key="ritual.id"
					:to="`/rituals/${ritual.id}`"
					class="px-4"
				>
					<template #prepend>
						<v-icon
							size="18"
							:color="ritual.ritualType?.spiritualLine?.primaryColor || 'primary'"
						>
							mdi-calendar-star
						</v-icon>
					</template>
					<v-list-item-title class="text-body-2 font-weight-medium">
						{{ ritual.title }}
					</v-list-item-title>
					<v-list-item-subtitle class="text-caption">
						{{ ritual.ritualType?.name }}
						<span class="mx-1">&middot;</span>
						{{ formatDate(ritual.startDate) }}
					</v-list-item-subtitle>
				</v-list-item>
			</v-list>
		</v-card-text>

		<v-divider />

		<v-card-actions class="pa-2 justify-center">
			<v-btn variant="text" size="small" color="primary" to="/rituals">
				{{ $t("dashboard.upcomingRituals.viewAll") }}
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import ritualService, { type Ritual, RitualStatus } from "@/services/ritualService";
import { useDateFormat } from "@/composables/useDateFormat";

const loading = ref(true);
const rituals = ref<Ritual[]>([]);
const { formatTimestamp } = useDateFormat();

function formatDate(dateStr: string): string {
	return formatTimestamp(dateStr, {
		day: "2-digit",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
	});
}

onMounted(async () => {
	try {
		const result = await ritualService.getRituals({
			status: RitualStatus.SCHEDULED,
			isActive: true,
			limit: 3,
			sortBy: "date",
			sortOrder: "asc",
		});
		rituals.value = result.data;
	} catch {
		// silently fail — widget is non-critical
	} finally {
		loading.value = false;
	}
});
</script>
